import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, AttemptBadge, AuditedBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Search, ClipboardCheck, FileDown, MessageSquare, BarChart3, Users, BookOpen } from "lucide-react";

const RUBRIC = [
  { key: "apertura", label: "Apertura y encuadre" },
  { key: "preguntas", label: "Calidad de preguntas" },
  { key: "empatia", label: "Empatía y reflejo" },
  { key: "exploracion", label: "Exploración del motivo" },
  { key: "cierre", label: "Cierre de sesión" },
];

export default function Supervisor() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState("");
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    const { data } = await api.get(`/supervisor/sessions?${params.toString()}`);
    setRows(data);
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const loadStats = async () => {
    const { data } = await api.get("/supervisor/stats");
    setStats(data);
  };

  const openTranscript = async (row) => {
    const { data } = await api.get(`/sessions/${row.id}`);
    setSelected(data);
    const ev = data.evaluation;
    setScores(ev?.scores || { apertura: 3, preguntas: 3, empatia: 3, exploracion: 3, cierre: 3 });
    setComments(ev?.comments || "");
  };

  const submitGrade = async () => {
    try {
      await api.post(`/supervisor/sessions/${selected.id}/grade`, { scores, comments });
      toast.success("Calificación guardada");
      load();
      setSelected((s) => ({ ...s, evaluation: { scores, comments } }));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (row) => {
    const res = await api.get(`/sessions/${row.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${row.public_id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const total = RUBRIC.reduce((sum, r) => sum + (scores[r.key] || 0), 0);

  return (
    <AppShell title="Revisión de diálogos">
      <Tabs defaultValue="dialogs" onValueChange={(v) => v === "stats" && loadStats()}>
        <TabsList className="mb-5">
          <TabsTrigger value="dialogs"><ClipboardCheck className="mr-1.5 h-4 w-4" />Diálogos</TabsTrigger>
          <TabsTrigger value="stats" data-testid="supervisor-stats-tab"><BarChart3 className="mr-1.5 h-4 w-4" />Estadísticas</TabsTrigger>
        </TabsList>
        <TabsContent value="dialogs">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="supervisor-search-input"
            placeholder="Buscar por ID de diálogo o matrícula..."
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="supervisor-filter-exercise">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="in_progress">En progreso</SelectItem>
            <SelectItem value="finished">Finalizada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Matrícula</TableHead>
              <TableHead>Ejercicio</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Calif.</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Sin resultados</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid="supervisor-transcript-row" className="cursor-pointer">
                <TableCell className="font-mono text-xs text-primary">{r.public_id}</TableCell>
                <TableCell className="text-sm">{r.student_matricula}</TableCell>
                <TableCell className="text-sm">{r.exercise_title}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>
                  {r.graded
                    ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Calificado</Badge>
                    : <Badge variant="outline" className="border-slate-200 text-slate-400">Pendiente</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Sheet onOpenChange={(o) => o && openTranscript(r)}>
                      <SheetTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="rubric-grade-drawer-trigger">
                          <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Revisar
                        </Button>
                      </SheetTrigger>
                      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl sepp-scroll">
                        <SheetHeader>
                          <SheetTitle className="font-display">Revisión · {selected?.public_id}</SheetTitle>
                        </SheetHeader>
                        {selected && (
                          <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_260px]">
                            <div>
                              <div className="mb-3 flex flex-wrap gap-2">
                                <AttemptBadge n={selected.attempt_number} />
                                <AuditedBadge audited={selected.audited} />
                                <Badge variant="outline">{selected.patient_name}</Badge>
                              </div>
                              <div className="sepp-scroll max-h-[60vh] space-y-3 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4">
                                {(selected.messages || []).filter((m) => m.role !== "system").map((m, i) => (
                                  <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "student" ? "bg-primary text-primary-foreground" : "border border-border bg-white"}`}>
                                      <p className={`mb-0.5 text-[10px] uppercase tracking-wider ${m.role === "student" ? "text-teal-100" : "text-violet-600"}`}>
                                        {m.role === "student" ? "Terapeuta" : "Paciente"}
                                      </p>
                                      {m.content}
                                    </div>
                                  </div>
                                ))}
                                {(selected.messages || []).filter((m) => m.role !== "system").length === 0 && (
                                  <p className="py-6 text-center text-sm text-muted-foreground"><MessageSquare className="mx-auto mb-2 h-5 w-5" />Sin mensajes</p>
                                )}
                              </div>
                            </div>

                            <div className="space-y-4 rounded-lg border border-border bg-card p-4">
                              <p className="font-display text-sm font-semibold">Rúbrica de evaluación</p>
                              {RUBRIC.map((r2) => (
                                <div key={r2.key} className="space-y-1.5">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">{r2.label}</span>
                                    <span className="font-mono font-semibold text-primary">{scores[r2.key] ?? 0}/5</span>
                                  </div>
                                  <Slider
                                    data-testid="rubric-score-input"
                                    min={0} max={5} step={1}
                                    value={[scores[r2.key] ?? 0]}
                                    onValueChange={(v) => setScores((s) => ({ ...s, [r2.key]: v[0] }))}
                                  />
                                </div>
                              ))}
                              <div className="flex items-center justify-between rounded-md bg-primary/5 px-3 py-2">
                                <span className="text-xs font-medium">Total</span>
                                <span className="font-mono text-sm font-bold text-primary">{total}/25</span>
                              </div>
                              <Textarea placeholder="Comentarios..." value={comments} onChange={(e) => setComments(e.target.value)} className="min-h-[80px]" />
                              <Button className="w-full" onClick={submitGrade} data-testid="rubric-submit-button">Guardar calificación</Button>
                            </div>
                          </div>
                        )}
                      </SheetContent>
                    </Sheet>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)}>
                      <FileDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>

        <TabsContent value="stats">
          <StatsView stats={stats} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

const CRIT_LABELS = {
  apertura: "Apertura", preguntas: "Preguntas", empatia: "Empatía",
  exploracion: "Exploración", cierre: "Cierre",
};

function StatsView({ stats }) {
  if (!stats) return <p className="py-10 text-center text-sm text-muted-foreground">Cargando estadísticas...</p>;
  const { totals, by_exercise, by_student, criteria } = stats;

  const Group = ({ title, icon: Icon, rows }) => (
    <div className="rounded-xl border border-border bg-card p-6">
      <p className="mb-4 flex items-center gap-2 font-display font-semibold"><Icon className="h-4 w-4 text-primary" />{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aún no hay sesiones calificadas.</p>
      ) : (
        <div className="space-y-5">
          {rows.map((r) => (
            <div key={r.name} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{r.name}</span>
                <span className="text-muted-foreground">{r.avg_total}/25 · <span className="text-xs">{r.count} calif.</span></span>
              </div>
              <Progress value={(r.avg_total / 25) * 100} className="h-2" />
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                {criteria.map((c) => (
                  <span key={c} className="text-xs text-muted-foreground">
                    {CRIT_LABELS[c]}: <span className="font-mono font-medium text-foreground">{r.avg_scores[c]}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="supervisor-stats-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        {[["Sesiones totales", totals.sessions], ["Finalizadas", totals.finished], ["Calificadas", totals.graded]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{l}</p>
            <p className="mt-1 font-display text-3xl font-bold text-primary">{v}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Group title="Promedio por ejercicio" icon={BookOpen} rows={by_exercise} />
        <Group title="Promedio por alumno" icon={Users} rows={by_student} />
      </div>
    </div>
  );
}
